import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const PAIN_POINTS = [
  "ISO 17025 accreditation documentation gaps",
  "Method validation records that are incomplete or outdated",
  "QA records not audit-ready for ILAC or A2LA assessments",
  "Lean teams unable to keep up with documentation requirements",
  "Inconsistent SOP versions across testing departments",
];

export default function EnvironmentalTestingLabs() {
  return (
    <>
      <SEO
        title="ISO 17025 Compliance for Environmental & Testing Labs | LabComplyx"
        description="LabComplyx helps environmental and testing labs achieve ISO 17025 compliance with governed documentation workflows, method validation records, and audit-ready QA outputs."
        canonical="https://labcomplyx.com/industries/environmental-testing"
      />
      <div className="pt-24">
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">ISO 17025 · NELAC</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5 leading-tight">
                ISO 17025 compliance for environmental and testing labs
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                LabComplyx helps accredited testing labs reduce documentation backlog, improve method records, and stay audit-ready — without the overhead of a large QA team.
              </p>
              <Link to="/ValidationIntake">
                <Button size="lg" className="gap-2">
                  Book a Free Compliance Review <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Documentation challenges for testing labs</h2>
                <ul className="space-y-3">
                  {PAIN_POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">How LabComplyx supports ISO 17025 labs</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  We provide structured documentation workflows aligned to ISO 17025 clause requirements. Every output is human-reviewed and traceable, so your records are defensible when assessors arrive.
                </p>
                <ul className="space-y-2.5">
                  {["ISO 17025 clause-aligned documentation", "Method validation and verification records", "QA manual and SOP drafting support", "Nonconformance and corrective action records", "Audit-ready evidence packages"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Not a LIMS statement + capabilities + blog links */}
        <section className="py-10 bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-muted/40 border border-border rounded-xl p-5 max-w-2xl mb-8">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">LabComplyx is not a LIMS or full eQMS replacement.</strong> It is a governed documentation layer that works alongside your existing laboratory information system — reducing documentation backlog and improving audit readiness without requiring a platform change.
              </p>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-4">Related capabilities</h2>
            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { label: "Inspection Readiness", href: "/solutions/inspection-readiness" },
                { label: "SOP Management", href: "/solutions/sop-management" },
                { label: "Deviation Documentation", href: "/solutions/deviation-documentation" },
                { label: "Validation Support", href: "/solutions/validation-support" },
              ].map((c) => (
                <Link key={c.href} to={c.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">
                  {c.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
            <h3 className="text-base font-semibold text-foreground mb-3">Relevant guides & resources</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { label: "Lab SOP Management Guide", href: "/blog/lab-sop-management" },
                { label: "CLIA Gap Audit (transferable to ISO 17025)", href: "/blog/clia-gap-audit" },
              ].map((b) => (
                <Link key={b.href} to={b.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-muted/30 hover:border-primary/40 hover:text-primary transition-colors">
                  {b.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Reduce your ISO 17025 documentation risk</h2>
            <p className="text-primary-foreground/80 mb-8">Book a free compliance review to identify documentation gaps before your next A2LA or ILAC assessment.</p>
            <Link to="/ValidationIntake">
              <Button size="lg" variant="secondary" className="gap-2">
                Book a Free Compliance Review <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}